import "./App.css";
import config from "./config.ts";

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { AgentNavigationItems } from "./constants/Navigation";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { ThemeProvider } from "./context/themeContext";
import { UserProvider } from "./context/userContext";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Loader from './components/Common/Loader';
import PatternOfLifeTest from "./pages/PatternOfLifeTest.jsx";

const ApplicantHistory = lazy(() => import("./components/Applicants/ApplicantHistory"));
const AgentLayout = lazy(() => import("./components/Layout/AgentLayout"));
const Login = lazy(() => import("./components/Login/Login"));
const SafePathLoginCallback = lazy(() => import("./components/Login/SafePathLoginCallback"));
const SecureRoute = lazy(() => import("./components/Login/SecureRoute"));
const BondDetails = lazy(() => import("./components/Applicants/BondDetails.jsx"));
const CheckinDetails = lazy(() => import("./components/Applicants/CheckinDetails.jsx"));
const BondApplication = lazy(() => import("./components/Applicants/NewBondApplication.jsx"));
const InPersonCheckin = lazy(() => import("./components/Applicants/InPersonCheckin.jsx"));
const AdhocVideoCheckIn = lazy(() => import("./components/Applicants/AdhocVideoCheckIn.jsx"));
const HomeDashboard = lazy(() => import("./components/HomePage/HomeDashboard"));
const MessageCenterLanding = lazy(() => import("./components/MessageCenter/MessageCenterLanding.jsx"));
const EventCenterLanding = lazy(() => import("./components/EventCenter/EventCenterLanding.jsx"));
const PaymentCenterLanding = lazy(() => import("./components/PaymentCenter/PaymentCenterLanding.jsx"));
const VideoSession = lazy(() => import("./components/VideoSession/VideoSession.jsx"));
const WaitingRoom = lazy(() => import("./components/VideoSession/WaitingRoom.jsx"));
const CameraTest = lazy(() => import("./components/VideoSession/CameraTest.jsx"));
const GuestJoin = lazy(() => import("./components/Guest/GuestJoin.jsx"));
const GuestVideoSession = lazy(() => import("./components/Guest/GuestVideoSession.jsx"));
const GuestEnded = lazy(() => import("./components/Guest/GuestEnded.jsx"));
const BailSettings = lazy(() => import("./components/Bail/BailSettings.jsx"));
const AgentDailyDigest = lazy(() => import("./components/Bail/AgentDailyDigest.jsx"));
const DefendantHistory = lazy(() => import("./components/Bail/DefendantHistory.jsx"));
const DefendantLocations = lazy(() => import("./components/Bail/DefendantLocations.jsx"));
const BailSchedule = lazy(() => import("./components/Bail/BailSchedule.jsx"));
const BailVideoCheckIn = lazy(() => import("./components/Bail/BailVideoCheckIn.jsx"));
const BailPhoneCheckIn = lazy(() => import("./components/Bail/BailPhoneCheckIn.jsx"));
const PrivacyPolicy = lazy(() => import("./components/Legal/PrivacyPolicy.jsx"));
const SupportPage = lazy(() => import("./components/Legal/SupportPage.jsx"));
const MobileApp = lazy(() => import("./components/MobileApp/MobileApp.jsx"));
const UserProfile = lazy(() => import("./components/UserProfile/UserProfile.jsx"));
const FeedbackWidget = lazy(() => import("./components/Feedback/FeedbackWidget.jsx"));
const NotFound = lazy(() => import("./components/NotFound/NotFound.jsx"));
const EngagementScript = lazy(() => import("./components/Onboarding/EngagementScript.jsx"));
const AnnouncementManager = lazy(() => import("./components/Announcements/AnnouncementManager.jsx"));
const CategoryManager = lazy(() => import("./components/Announcements/CategoryManager.jsx"));


function App() {
    const navigate = useNavigate();

    const oidcConfig = config.oidc;
    oidcConfig.redirectUri = `${window.location.origin}/login/callback`;
    const oktaAuth = new OktaAuth(oidcConfig);

    const restoreOriginalUri = (_oktaAuth, originalUri) => {
        // BUG-1110: return the agent to the deep link they were sent to (e.g. the daily-digest link
        // in the email), falling back to the dashboard for normal logins or root/login callback URIs.
        const relative = toRelativeUrl(originalUri || `${window.location.origin}/baildashboard`, window.location.origin);
        navigate(relative && relative !== "/" && !relative.startsWith("/login") ? relative : "/baildashboard");
    };

    function MainLayout() {
        const [accessToken, setAccessToken] = useState("");

        useEffect(() => {
            if (oktaAuth && oktaAuth.authState?.isAuthenticated) {
                getOktaTokens();
            }
        }, [oktaAuth]);

        const getOktaTokens = async () => {
            try {
                const { accessToken, idToken } = await oktaAuth.tokenManager.getTokens();
                setAccessToken(accessToken.accessToken);
            } catch (_err) {
                // Intentionally swallow token retrieval errors in production hardening pass.
            }
        };

        return (
            <>
                {/* STORY-978: small shared bottom padding at the app-shell level
                    for a tiny visual buffer below page content. The primary
                    "doesn't cover last control" guarantees come from the
                    user-movable + dismissible widget itself; this padding is
                    just a soft fallback. */}
                <div className="pb-2">
                <Routes>
                    {/* Routes without layout go here */}
                    <Route path="/" exact={true} element={<Login />} />
                    <Route
                        path="login/callback"
                        element={
                            <Suspense fallback={<Loader />}>
                                <SafePathLoginCallback />
                            </Suspense>
                        }
                    />
                    {/* Guest routes - no authentication required */}
                    <Route path="/join/:token" element={<GuestJoin />} />
                    <Route path="/guest-video/:roomId" element={<GuestVideoSession />} />
                    <Route path="/guest-ended" element={<GuestEnded />} />
                    {/* Legal pages - public for Apple review */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/support" element={<SupportPage />} />
                    {/* Public smart-link landing page for SMS/email. Auto-redirects to the
                        correct mobile app destination; falls back to a QR code on desktop. */}
                    <Route path="/mobileapp" element={<MobileApp />} />
                    <Route path="/not-found" element={<NotFound />} />
                    <Route
                        path="/*"
                        element={
                            <SecureRoute>
                                <AgentLayout>
                                    <Routes>
                                        {AgentNavigationItems.map((item) =>
                                            <Route
                                                key='item.route'
                                                path={item.route}
                                                element={<item.component />}
                                            />
                                        )}
                                        <Route path="home" element={<HomeDashboard />} />
                                        <Route path="/bailsettings" element={<BailSettings />} />
                                        <Route path="/agent-daily-digest" element={<AgentDailyDigest />} />
                                        <Route path="/bail/schedule" element={<BailSchedule />} />
                                        <Route path="/bail/videocheckin/:checkInId" element={<BailVideoCheckIn />} />
                                        <Route path="/bail/phonecheckin/:checkInId" element={<BailPhoneCheckIn />} />
                                        <Route path="/applicants/:id" element={<ApplicantHistory />} />
                                        <Route path="/defendants/:id" element={<DefendantHistory />} />
                                        <Route path="/locations/:alienId" element={<DefendantLocations />} />
                                        <Route path="/bonddetails/:id" element={<BondDetails />} />
                                        <Route path="/checkindetails/:id" element={<CheckinDetails />} />
                                        <Route path="/bondapplication" element={<BondApplication />} />
                                        <Route path="/inpersoncheckin/:checkInId" element={<InPersonCheckin />} />
                                        <Route path="/adhocvideocheckin/:checkInId" element={<AdhocVideoCheckIn />} />
                                        <Route path="/messagecenter/:alienId" element={<MessageCenterLanding />} />
                                        <Route path="/eventcenter/:alienId" element={<EventCenterLanding />} />
                                        <Route path="/paymentcenter/:alienId" element={<PaymentCenterLanding />} />
                                        <Route path="/videosession/:roomId" element={<VideoSession />} />
                                        <Route path="/waiting/:roomId" element={<WaitingRoom />} />
                                        <Route path="/camera-test" element={<CameraTest />} />
                                        <Route path="/profile" element={<UserProfile />} />
                                        <Route path="/announcements" element={<AnnouncementManager />} />
                                        <Route path="/announcement-categories" element={<CategoryManager />} />
                                        <Route path="/dashboard" element={<Navigate to="/baildashboard" replace />} />
                                        <Route path="*" element={<Navigate to="/not-found" replace />} />
                                        <Route path="/engagement-script" element={<EngagementScript />} />
                                        <Route path="/pol-test" element={<PatternOfLifeTest />} />
                                    </Routes>
                                </AgentLayout>
                            </SecureRoute>
                        }
                    />
                </Routes>
                </div>
                <FeedbackWidget />
                <ToastContainer />
            </>
        )
    };

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <UserProvider>
                <ThemeProvider>
                    <Suspense fallback={<Loader />}>
                        <MainLayout />
                    </Suspense>
                </ThemeProvider>
            </UserProvider>
        </Security>
    );
}

export default App;
